// i18n area 'catalogSources' (ENEA HP-SAN-3 R21, 2026-10-25,
// docs/enea-hp-r21-disegno-2026-10-25.md): the catalog source console — a
// filesystem ALREADY MOUNTED on one node (a Lustre client, an NFS export
// mounted by hand, any POSIX fs) catalogued by that node's worker in full
// mode by mtime into the same catalog database as its shares. The node and
// the mount point are chosen from the node's mount census, never typed. The
// catalog panel itself speaks storage.fileShares.catalog.* (shared with the
// share); `panel` holds only the sentences that differ for a source.

export const en = {
  catalogSources: {
    nav: 'Catalog sources',
    title: 'Catalog sources',
    hint: 'A filesystem ALREADY MOUNTED on one node (a Lustre client, an NFS export mounted by hand, any POSIX filesystem) declared as a source of that node’s catalog: the node’s worker walks it in full mode by mtime and loads the entries into the same catalog database as its shares — same tables, same export, same read-only SQL role (rows carry source = mount). The product never mounts, unmounts or writes inside the mount; admission is the node’s mount census, never a typed path.',
    name: 'Name',
    scope: 'Scope',
    node: 'Node',
    mount: 'Mount',
    mountPath: 'Mount point',
    fstype: 'Filesystem',
    fsSource: 'Mount source',
    subPath: 'Subtree',
    wholeMount: 'whole mount',
    root: 'Root walked',
    excludes: 'Excluded subtrees',
    excludesNone: 'none',
    state: 'State',
    createdAt: 'Created at',
    noPermission: 'Your grants do not cover this action here',
    deleteHint:
      'Deleting is immediate: a running scan is cancelled and the catalogued rows are dropped by the node’s next catalog tick. The mount itself is never touched.',
    states: {
      on: 'catalog on',
      off: 'catalog off',
      present: 'mounted',
      absent: 'NOT mounted',
      unknown: 'mount unknown',
      inService: 'node in service',
      outOfService: 'node NOT in service',
      maintenance: 'node in maintenance',
      scanning: 'scanning',
      neverScanned: 'never scanned',
      lastScan: 'last scan: {outcome}'
    },

    createDialog: {
      title: 'Create catalog source',
      name: 'Name',
      nameHint: 'DNS-1123 label, unique in the scope',
      node: 'Node',
      nodeHint: 'The owner node: the member that mounts the filesystem. Fixed for the life of the source.',
      noNodes: 'No cluster node in reach',
      ineligible: '{name} — {reason}',
      reasons: { member: 'not a member', maintenance: 'in maintenance' },
      mount: 'Mount point',
      mountHint:
        'One of the mounts the node’s census reports (findmnt): the source must EQUAL a mount point — a subtree is said below. The product’s own trees (/, /proc, /sys, /dev, /boot, the system path) are refused by the server.',
      censusLoading: 'Reading the node’s mount census…',
      censusNever:
        'This node has never published a mount census (its worker is not sampling, or predates store v52): no catalog source can be admitted on it.',
      censusEmpty: 'The node’s census lists no mount outside the pseudo-filesystems.',
      censusTruncated: 'The census is truncated at 512 entries: mounts beyond the cap are not admissible.',
      censusObserved: 'Census of {at} via {via}',
      censusError: 'The census could not be read: {detail}',
      subPath: 'Subtree (optional)',
      subPathHint: 'Relative to the mount point, exact components (projects/x); empty = the whole mount',
      excludes: 'Excluded subtrees (optional, one per line)',
      excludesHint:
        'Relative to the root walked (mount + subtree): the directory entry stays, nothing below it is catalogued. Exact components, not globs (a/b excludes a/b, not a/bc); at most 64.',
      notes: 'Notes',
      refusals: {
        name: 'The name is a DNS-1123 label',
        node: 'Choose the owner node',
        mount: 'Choose a mount point from the node’s census',
        subPathAbsolute: 'The subtree is relative: no leading /',
        subPathSlash: 'The subtree has no trailing /',
        subPathDot: 'The subtree has no empty, . or .. component',
        subPathTooLong: 'The subtree is at most 1024 bytes',
        excludesTooMany: 'At most 64 excluded subtrees',
        excludeAbsolute: 'An excluded subtree is relative: no leading /',
        excludeSlash: 'An excluded subtree has no trailing /',
        excludeDot: 'An excluded subtree has no empty, . or .. component',
        excludeTooLong: 'An excluded subtree is at most 1024 bytes',
        policy: 'The policy is out of its bounds (cadence 15–10080 minutes, cap ≥ 1000, no incremental on a mount)'
      }
    },

    editDialog: {
      title: 'Edit {name}',
      immutable:
        'Node and mount point are immutable: to move the source, delete it and create it again. The change is re-admitted against the node’s current census.'
    },

    tabs: {
      overview: 'Source',
      catalog: 'Catalog'
    },

    panel: {
      intro:
        'The catalog of this source lives in the catalog database of its node — the same tables, export and read-only SQL role as the node’s shares (rows carry source = mount). Every scan is FULL by mtime: a mount has no recursive ctime, so deletions are seen only by the next full pass (the incremental way for Lustre is its changelog, declared).',
      policyHint:
        'Auto and Full are both a full scan on a mount; the first scan runs on the node’s next tick once the catalog is on. Switching the catalog off keeps the rows already catalogued. The empty cap means 5 000 000 entries — a scan that reaches it closes «partial» and says so.',
      modeHint: 'Full by mtime (no incremental on a mount)',
      maxEntriesHint: 'Empty = 5 000 000 (the prudent default of a source; up to 10⁹)',
      mountPresent: 'mounted',
      mountAbsent: 'NOT mounted — the next scan fails without touching a row',
      mountLine: 'Mount: {fstype} from {source} ({options})',
      censusSeen: 'census of {at}',
      nodeInService: 'node in service',
      nodeOutOfService: 'node NOT in service — no scan is born',
      nodeMaintenance: 'node in maintenance — no new scan is born'
    }
  }
}

export const it: typeof en = {
  catalogSources: {
    nav: 'Sorgenti di catalogo',
    title: 'Sorgenti di catalogo',
    hint: 'Un filesystem GIÀ MONTATO su un nodo (un client Lustre, un export NFS montato a mano, qualunque filesystem POSIX) dichiarato sorgente del catalogo di quel nodo: il worker del nodo lo percorre in modalità piena per mtime e carica le voci nello stesso database di catalogo delle sue share — stesse tabelle, stesso export, stesso ruolo SQL in sola lettura (le righe portano source = mount). Il prodotto non monta, non smonta e non scrive mai dentro il mount; l’ammissione è il censimento dei mount del nodo, mai un percorso scritto a mano.',
    name: 'Nome',
    scope: 'Scope',
    node: 'Nodo',
    mount: 'Mount',
    mountPath: 'Punto di mount',
    fstype: 'Filesystem',
    fsSource: 'Sorgente del mount',
    subPath: 'Sottoalbero',
    wholeMount: 'tutto il mount',
    root: 'Radice percorsa',
    excludes: 'Sottoalberi esclusi',
    excludesNone: 'nessuno',
    state: 'Stato',
    createdAt: 'Creata il',
    noPermission: 'I tuoi grant non coprono questa azione qui',
    deleteHint:
      'La cancellazione è immediata: una scansione in corso viene annullata e le righe catalogate vengono tolte al prossimo tick di catalogo del nodo. Il mount non viene mai toccato.',
    states: {
      on: 'catalogo acceso',
      off: 'catalogo spento',
      present: 'montato',
      absent: 'NON montato',
      unknown: 'mount ignoto',
      inService: 'nodo in servizio',
      outOfService: 'nodo NON in servizio',
      maintenance: 'nodo in manutenzione',
      scanning: 'in scansione',
      neverScanned: 'mai scansionata',
      lastScan: 'ultima scansione: {outcome}'
    },

    createDialog: {
      title: 'Crea sorgente di catalogo',
      name: 'Nome',
      nameHint: 'Etichetta DNS-1123, unica nello scope',
      node: 'Nodo',
      nodeHint: 'Il nodo proprietario: il membro che monta il filesystem. Fisso per tutta la vita della sorgente.',
      noNodes: 'Nessun nodo del cluster raggiungibile',
      ineligible: '{name} — {reason}',
      reasons: { member: 'non membro', maintenance: 'in manutenzione' },
      mount: 'Punto di mount',
      mountHint:
        'Uno dei mount che il censimento del nodo riporta (findmnt): la sorgente deve ESSERE UGUALE a un punto di mount — il sottoalbero si dice sotto. Gli alberi del prodotto (/, /proc, /sys, /dev, /boot, il system path) sono rifiutati dal server.',
      censusLoading: 'Leggo il censimento dei mount del nodo…',
      censusNever:
        'Questo nodo non ha mai pubblicato un censimento dei mount (il suo worker non campiona, o è precedente allo store v52): nessuna sorgente di catalogo può essere ammessa su di esso.',
      censusEmpty: 'Il censimento del nodo non elenca mount fuori dai pseudo-filesystem.',
      censusTruncated: 'Il censimento è troncato a 512 voci: i mount oltre il tetto non sono ammissibili.',
      censusObserved: 'Censimento del {at} via {via}',
      censusError: 'Il censimento non si è potuto leggere: {detail}',
      subPath: 'Sottoalbero (facoltativo)',
      subPathHint: 'Relativo al punto di mount, componenti esatti (projects/x); vuoto = tutto il mount',
      excludes: 'Sottoalberi esclusi (facoltativi, uno per riga)',
      excludesHint:
        'Relativi alla radice percorsa (mount + sottoalbero): la voce della directory resta, niente sotto di essa viene catalogato. Componenti esatti, non glob (a/b esclude a/b, non a/bc); al massimo 64.',
      notes: 'Note',
      refusals: {
        name: 'Il nome è un’etichetta DNS-1123',
        node: 'Scegli il nodo proprietario',
        mount: 'Scegli un punto di mount dal censimento del nodo',
        subPathAbsolute: 'Il sottoalbero è relativo: niente / iniziale',
        subPathSlash: 'Il sottoalbero non ha / finale',
        subPathDot: 'Il sottoalbero non ha componenti vuoti, . o ..',
        subPathTooLong: 'Il sottoalbero è al massimo 1024 byte',
        excludesTooMany: 'Al massimo 64 sottoalberi esclusi',
        excludeAbsolute: 'Un sottoalbero escluso è relativo: niente / iniziale',
        excludeSlash: 'Un sottoalbero escluso non ha / finale',
        excludeDot: 'Un sottoalbero escluso non ha componenti vuoti, . o ..',
        excludeTooLong: 'Un sottoalbero escluso è al massimo 1024 byte',
        policy:
          'La policy è fuori dalle sue bande (cadenza 15–10080 minuti, tetto ≥ 1000, niente incrementale su un mount)'
      }
    },

    editDialog: {
      title: 'Modifica {name}',
      immutable:
        'Nodo e punto di mount sono immutabili: per spostare la sorgente, cancellala e ricreala. La modifica viene riammessa contro il censimento corrente del nodo.'
    },

    tabs: {
      overview: 'Sorgente',
      catalog: 'Catalogo'
    },

    panel: {
      intro:
        'Il catalogo di questa sorgente vive nel database di catalogo del suo nodo — le stesse tabelle, lo stesso export e lo stesso ruolo SQL in sola lettura delle share del nodo (le righe portano source = mount). Ogni scansione è PIENA per mtime: un mount non ha un ctime ricorsivo, quindi le cancellazioni si vedono solo alla passata piena successiva (la via incrementale per Lustre è il suo changelog, dichiarata).',
      policyHint:
        'Auto e Piena sono entrambe una scansione piena su un mount; la prima scansione parte al prossimo tick del nodo quando il catalogo è acceso. Spegnere il catalogo conserva le righe già catalogate. Il tetto vuoto vale 5 000 000 voci — una scansione che lo raggiunge chiude «parziale» e lo dice.',
      modeHint: 'Piena per mtime (niente incrementale su un mount)',
      maxEntriesHint: 'Vuoto = 5 000 000 (il default prudente di una sorgente; fino a 10⁹)',
      mountPresent: 'montato',
      mountAbsent: 'NON montato — la prossima scansione fallisce senza toccare una riga',
      mountLine: 'Mount: {fstype} da {source} ({options})',
      censusSeen: 'censimento del {at}',
      nodeInService: 'nodo in servizio',
      nodeOutOfService: 'nodo NON in servizio — nessuna scansione nasce',
      nodeMaintenance: 'nodo in manutenzione — nessuna scansione nuova nasce'
    }
  }
}
